'use client';

import { useEffect, useState } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import dynamic from 'next/dynamic';
import { BackendAuth } from '@/lib/auth-backend';
import { OrganisationThemeProvider } from '@/providers/organisation-theme-provider';
import { NexusBackgroundProvider } from '@/providers/nexus-background-provider';
import { NotificationProvider } from '@/providers/notification-provider';
import { NexusCoreProvider } from '@/providers/nexus-core-provider';
import { PersonalAIProvider } from '@/providers/personal-ai-provider';
import { OnboardingProvider } from '@/providers/onboarding-provider';
import { ScreenLoader } from '@/components/common/screen-loader';
import { Demo1Layout } from '../components/layouts/demo1/layout';
import '@/css/driver-tour.css';
import { usePageTitle } from '@/hooks/usePageTitle';

// Lazy-load the always-mounted-but-on-demand-visible AI/onboarding leaf panels.
// They render as siblings of <Demo1Layout> (they do NOT wrap `children` and are
// NOT part of the auth gate), so deferring their compile lightens every
// protected route's first Turbopack compile without changing render behaviour.
// ssr:false is correct — these are interactive client-only panels.
// Nexus Core is a master-password-only power tool — it now lives ONLY at the /nexus/core
// full page (no floating button, no slide-in drawer). The old NexusCoreButton/NexusCorePanel
// mounts were removed per user decision (2026-06-17). The /nexus/core route still wraps itself
// in NexusCoreProvider (see the fullscreenPages branch) and only renders for nexus_core_access.
const OnboardingWidget = dynamic(() => import('@/components/onboarding/OnboardingWidget'), { ssr: false });
const PageTourLoader = dynamic(() => import('@/components/onboarding/PageTourLoader'), { ssr: false });
// End-user Personal-AI assistant: slide-in panel (right) with the agentic chat.
// Renders as a sibling of <Demo1Layout> inside <PersonalAIProvider> so the
// header's Sparkles button can read hasAccess + toggle it. Gated by
// (access-check-Roundtrip obsolet entfernt — Rechte via Rechtegruppen-Enforcement).
const PersonalAIPanel = dynamic(() => import('@/components/personal-ai/PersonalAIPanel'), { ssr: false });
// Telefon der Telefonanlage (Modul pbx) — erscheint nur bei installiertem Modul + zugeordneter Nebenstelle.
const PhoneDock = dynamic(() => import('@/components/phone/PhoneDock'), { ssr: false });

export default function ProtectedLayout({ children }) {
  // Dynamischer Seitentitel basierend auf Menüpunkt + Organisation
  usePageTitle();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Embed-Modus: Seite wird in iframe eingebettet (Laravel-iframe nutzt `embed=1`,
  // Marketplace-Module-Dashboards nutzen `embedded=1` — beide deaktivieren Sidebar/Header).
  const isEmbedMode = searchParams?.get('embed') === '1' || searchParams?.get('embedded') === '1';
  const embedSessionId = searchParams?.get('_es');

  useEffect(() => {
    // Check BackendAuth authentication
    const checkAuth = async () => {
      const authenticated = BackendAuth.isAuthenticated();
      setIsAuthenticated(authenticated);

      // Helper to get safe return URL (only pathname, no query params that could cause loops)
      const getSafeReturnUrl = () => {
        // Only use pathname to prevent loops with nested return params
        const returnPath = pathname || '/dashboard';
        // Never redirect back to signin (prevents infinite loop)
        if (returnPath.startsWith('/signin') || returnPath.startsWith('/signup')) {
          return '/dashboard';
        }
        return returnPath;
      };

      if (!authenticated) {
        // Safari Embed-Fallback: Auth über Server-Session-ID (_es Param)
        if (isEmbedMode && embedSessionId) {
          try {
            const res = await fetch(`/api/embed/auth/session?_es=${encodeURIComponent(embedSessionId)}`);
            const data = await res.json();
            if (data.success) {
              // Versuche localStorage zu setzen (kann in Safari noch nicht funktionieren)
              try {
                localStorage.setItem('auth_token', data.token);
                localStorage.setItem('user_data', JSON.stringify(data.user));
              } catch { /* Safari kann das noch blockieren — kein Problem */ }
              setIsAuthenticated(true);
              setIsLoading(false);
              return;
            }
          } catch { /* Session-Verify fehlgeschlagen */ }
        }
        // Normaler Fall: zu Login weiterleiten (kein Embed-Mode)
        const returnUrl = getSafeReturnUrl();
        router.push(`/signin?return=${encodeURIComponent(returnUrl)}`);
        return;
      }

      const onboardingExempt = ['/admin/company/onboarding', '/admin/company', '/signin', '/signup'];
      const isOnboardingExempt = onboardingExempt.some(p => pathname?.startsWith(p));

      // Verify token is still valid with backend.
      // IMPORTANT: the health check (and the user_data sync it performs) runs
      // BEFORE the onboarding gate. Previously the gate read stale localStorage
      // (org_onboarding_completed:false) and redirected to /admin/company/onboarding
      // before the health sync could ever correct the value — a permanent loop
      // for users who completed onboarding in another tab/session. Sync first,
      // then gate against the FRESH value.
      try {
        const response = await BackendAuth.apiCall('/api/auth/health');

        // Backend offline (503) - just finish loading, don't redirect.
        // Fall back to the (possibly stale) localStorage gate so we don't strand
        // a genuinely-not-onboarded user, but never loop a completed user just
        // because the backend blipped: only redirect on a strict `false`.
        if (response?.status === 503) {
          if (!isOnboardingExempt && BackendAuth.getUser()?.org_onboarding_completed === false) {
            router.push('/admin/company/onboarding');
          }
          setIsLoading(false);
          return;
        }

        // Only redirect on real auth errors (401) or no response
        if (!response || response.status === 401) {
          // console.warn('[ProtectedLayout] Health check failed - redirecting to login');
          BackendAuth.logout();
          const returnUrl = getSafeReturnUrl();
          router.push(`/signin?return=${encodeURIComponent(returnUrl)}`);
          return;
        }

        // Sync org_onboarding_completed from backend to localStorage (fresh truth)
        let freshUser = BackendAuth.getUser();
        try {
          const healthData = await response.json();
          if (healthData?.org_onboarding_completed !== undefined) {
            if (freshUser && freshUser.org_onboarding_completed !== healthData.org_onboarding_completed) {
              freshUser.org_onboarding_completed = healthData.org_onboarding_completed;
              localStorage.setItem('user_data', JSON.stringify(freshUser));
            }
          }
        } catch { /* ignore parse errors */ }

        // Onboarding gate AFTER the sync — checked against the fresh value.
        if (!isOnboardingExempt && freshUser?.org_onboarding_completed === false) {
          router.push('/admin/company/onboarding');
          setIsLoading(false);
          return;
        }

        setIsLoading(false);
      } catch (error) {
        console.error('[ProtectedLayout] Health check error:', error);
        // Don't logout on network errors - only on 401 (handled by BackendAuth.apiCall)
        setIsLoading(false);
      }
    };

    checkAuth();

    // Periodic health check every 60 seconds
    const healthCheckInterval = setInterval(async () => {
      if (BackendAuth.isAuthenticated()) {
        try {
          const response = await BackendAuth.apiCall('/api/auth/health');

          // Backend offline (503) - ignore, don't logout
          if (response?.status === 503) {
            return;
          }

          // Only logout/redirect on real auth errors (401) or no response
          if (!response || response.status === 401) {
            // console.warn('[ProtectedLayout] Periodic health check failed - token expired');
            BackendAuth.logout();
            // Pfad + Query (ohne verschachteltes return) -> gleiche Aktion nach dem Login
            const safeReturn = BackendAuth.buildReturnPath();
            router.push(`/signin?return=${encodeURIComponent(safeReturn)}`);
          }

          // Keep CSRF token fresh - refresh if less than 30 min left
          BackendAuth.refreshCsrfTokenIfOld(30 * 60 * 1000);
        } catch (error) {
          console.error('[ProtectedLayout] Periodic health check error:', error);
          // Don't logout on network errors
        }
      }
    }, 60000); // Every 60 seconds

    return () => clearInterval(healthCheckInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return <ScreenLoader />;
  }

  if (!isAuthenticated) {
    return <ScreenLoader />;
  }

  // Embed-Modus: Seite wird in Laravel-iframe eingebettet — kein Sidebar/Header
  if (isEmbedMode) {
    return <>{children}</>;
  }

  if (BackendAuth.getUser()?.khg_supplier) {
    // Lieferanten-Session: KEIN Header/Sidebar (kein Zugriff aufs KHG-Menue),
    // aber ein Abmelden muss es geben — sonst kommt der Lieferant aus der
    // Session nicht mehr raus (User-Befund 22.09.2026).
    const supplierBar = (
      <div className="flex justify-end border-b border-gray-200 bg-white px-4 py-2">
        <button
          type="button"
          onClick={() => { BackendAuth.logout(); window.location.replace('/signin'); }}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          Abmelden
        </button>
      </div>
    );
    if (pathname === '/khg/bonusanforderung-formular') return <>{supplierBar}{children}</>;
    // Offertanforderung (07.10.2026): Lieferant gibt sein Offert in der Offert-Maske ein.
    if (/^\/khg\/offert\/\d+\/\d+$/.test(pathname || '')) return <>{supplierBar}{children}</>;
    // Umsatzmeldung (19.10.2026): Lieferant landet per Link in /khg/umsaetze?anfrage=..&lieferant=..
    if (pathname === '/khg/umsaetze') return <>{supplierBar}{children}</>;
    return <>{supplierBar}<main className="p-6">Bitte öffnen Sie Ihre Anforderung über den Link in Ihrer E-Mail.</main></>;
  }

  // Fullscreen pages without layout (no sidebar/menu, but still authenticated).
  // Nexus Core lives at /nexus/core AND /nexus/core/{session-id} (catch-all route
  // [[...session]]). Both must render the SAME fullscreen shell — so we prefix-match
  // /nexus/core instead of relying on the exact-match list below. Without this, the
  // deep-linked /nexus/core/{session-id} URL fell through to <Demo1Layout> and showed
  // the header/menu/footer, while /nexus/core (exact) did not. (fix 2026-07-15)
  const isNexusCore = pathname === '/nexus/core' || pathname?.startsWith('/nexus/core/');
  if (isNexusCore) {
    // /nexus/core needs NexusCoreProvider for closePanel()
    return (
      <NexusCoreProvider>
        {children}
      </NexusCoreProvider>
    );
  }

  // Other fullscreen pages without layout (no sidebar/menu, but still authenticated)
  const fullscreenPages = [
    '/booking/calendar',
    '/admin/email/templates/preview',
    '/admin/developer-plan',  // Developer plan selection (admin-only, iframe to container)
    '/admin/enduser-plan',    // Enduser plan selection (admin-only, iframe to container)
  ];
  if (fullscreenPages.includes(pathname)) {
    return children;
  }

  // Use Demo1Layout like in the original working commit
  return (
    <OrganisationThemeProvider>
      <NexusBackgroundProvider>
        <NotificationProvider>
          <NexusCoreProvider>
            <PersonalAIProvider>
              <OnboardingProvider>
                <Demo1Layout>
                  {children}
                </Demo1Layout>
                <OnboardingWidget />
                <PageTourLoader />
                {/* End-user Personal-AI panel — sibling of the layout (does NOT
                    wrap children). Toggled from the header Sparkles button via
                    the provider's 'personal-ai:toggle' contract. */}
                <PersonalAIPanel />
                <PhoneDock />
              </OnboardingProvider>
            </PersonalAIProvider>
          </NexusCoreProvider>
        </NotificationProvider>
      </NexusBackgroundProvider>
    </OrganisationThemeProvider>
  );
}
